import { Button } from "@/components/ui/button";
import { useDisplayOptionContext } from "@/hooks/useDisplayOptionContext";
import { useRentalAction } from "@/hooks/useRentalAction";
import { useReservationAction } from "@/hooks/useReservationAction";
import { useTriggerButtonVisibility } from "@/hooks/useTriggerButtonVisibility";
import type { PromoExpandedBy } from "@/context/UnitItemContext";
import type { UnitGroup, UnitPromotion } from "@/types";

interface PromoExpandedContentProps {
    promotions: UnitPromotion[];
    selectedPromo: number;
    onPromoChange: (promoId: number) => void;
    unitGroup: UnitGroup;
    // Which inline button opened the selector — drives the prefix + action button.
    expandedBy: PromoExpandedBy;
    // Stepped quote flow: Continue advances to the in-row quote form.
    onRequestQuoteForm?: () => void;
    // Stepped reserve flow: Continue advances to the in-row reserve form.
    onRequestReserveForm?: () => void;
}

export const PromoExpandedContent = ({
    promotions,
    selectedPromo,
    onPromoChange,
    unitGroup,
    expandedBy,
    onRequestQuoteForm,
    onRequestReserveForm,
}: PromoExpandedContentProps) => {
    const { language } = useDisplayOptionContext();
    const { openRentalFunnel, openVbp, label: rentLabel } = useRentalAction(
        unitGroup,
        selectedPromo,
    );
    const { label: reserveLabel } = useReservationAction(
        unitGroup,
        selectedPromo,
    );
    const { hasMultipleSubGroups, shouldShowSelectButton } =
        useTriggerButtonVisibility(unitGroup);
    const quoteLabel = language?.action?.quoteButton ?? "Get a Quote";
    const selectLabel = language?.action?.selectButton ?? "Select";

    // One "Continue" matching the opening action; same onClick (tracking intact). No prefix for Select.
    let headingPrefix: string;
    let actionLabel: string;
    let onContinue: (() => void) | undefined;
    if (expandedBy === "quote") {
        headingPrefix = `${quoteLabel}: `;
        actionLabel = quoteLabel;
        onContinue = onRequestQuoteForm;
    } else if (expandedBy === "reserve") {
        headingPrefix = `${reserveLabel}: `;
        actionLabel = reserveLabel;
        onContinue = onRequestReserveForm;
    } else if (shouldShowSelectButton()) {
        headingPrefix = "";
        actionLabel = selectLabel;
        onContinue = hasMultipleSubGroups ? openVbp : openRentalFunnel;
    } else {
        headingPrefix = `${rentLabel}: `;
        actionLabel = rentLabel;
        onContinue = hasMultipleSubGroups ? openVbp : openRentalFunnel;
    }

    return (
        <>
            <h4 className="promotion-selection__title">
                {headingPrefix}Choose your promotional discount.
            </h4>
            <div
                className="promotion-selection__list"
                role="radiogroup"
                aria-label="Promotional discounts"
            >
                {promotions.map((promo) => (
                    <label key={promo.id}>
                        <input
                            type="radio"
                            name={`promo-${unitGroup.representative_unit.id}`}
                            value={promo.id}
                            checked={selectedPromo === promo.id}
                            onChange={() => onPromoChange(promo.id)}
                        />
                        {promo.name}
                    </label>
                ))}
            </div>
            {/* Placeholder for pricing transparency text — content TBD, hidden when empty via CSS */}
            <div className="promotion-transparency-text" />
            {/* Single action matching the inline button that opened the selector. */}
            <Button
                variant="default"
                className="promo-continue-btn"
                data-testid="selectorContinueButton"
                onClick={onContinue}
                type="button"
                aria-label={`Continue to ${actionLabel}`}
            >
                Continue
            </Button>
        </>
    );
};
